import React from 'react';
import { useFi } from '../../contexts/fi-context';
import { useFiResourcesPanel } from '../../hooks/api/usePeersonaResources';
import { PeersonaResourcesPanel } from '../peersona/peersona-resources-panel';
import { PEERSONA_DIVIDER, PEERSONA_TEXT_TERTIARY } from '../peersona/peersona-styles';
import { FiBlobAvatar } from './fi-blob-avatar';
import { FI_SUGGESTED_SEARCHES } from './fi-starter-actions';
import { FI_SCOPE_LINE } from './fi-about-copy';
import { cn } from '../../utils/cn';

interface FiWelcomeHeroProps {
  onLearnMore?: () => void;
}

/**
 * Persistent Fi welcome hero. Greeting + suggested searches scroll away;
 * the Learn more / Resources strip pins once the hero scrolls past (Peersona parity).
 */
export const FiWelcomeHero: React.FC<FiWelcomeHeroProps> = ({ onLearnMore }) => {
  const { applyStarterAction, isSending } = useFi();
  const {
    open: resourcesOpen,
    toggle: toggleResources,
    close: closeResources,
    resources,
    isLoading,
    isResorting,
    highlightResources,
  } = useFiResourcesPanel();

  return (
    <>
      <div aria-label="Welcome from Fi" role="region" className="flex flex-col items-center px-4 pb-4 pt-8 text-center">
        <FiBlobAvatar size={72} />
        <div className="font-albert-sans mt-6 inline-flex items-center gap-2 text-lg font-semibold text-black">
          <span>Hi, I&rsquo;m</span>
          <span className="inline-flex items-center gap-2 rounded-lg bg-orange-50 px-2 py-1 text-orange-900">Fi</span>
        </div>
        <p className="font-open-sans mt-1 text-xs font-semibold uppercase tracking-[0.14em] text-orange-600">
          Concierge
        </p>
        <p className="font-open-sans mt-4 max-w-[28rem] text-base leading-snug text-gray-600">{FI_SCOPE_LINE}</p>

        <div className="mt-6 w-full max-w-[28rem] text-left">
          <p className="font-albert-sans text-sm font-semibold text-gray-900">Suggested searches</p>
          <div className="mt-2 flex flex-col gap-2">
            {FI_SUGGESTED_SEARCHES.map((search) => (
              <button
                key={search.title}
                type="button"
                disabled={isSending}
                onClick={() => applyStarterAction(search)}
                className="font-open-sans hover:bg-concierge-pale flex w-full items-center justify-between rounded-xl border border-gray-200 bg-white px-4 py-3 text-left text-sm text-gray-800 transition-colors hover:border-orange-200 disabled:opacity-50"
              >
                <span>{search.title}</span>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden
                  className="shrink-0 text-gray-400"
                >
                  <path
                    d="M9 6l6 6-6 6"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div
        className="sticky top-0 z-[2] bg-white"
        style={{
          borderTop: `1px solid ${PEERSONA_DIVIDER}`,
          borderBottom: `1px solid ${PEERSONA_DIVIDER}`,
        }}
      >
        {onLearnMore ? (
          <div className="flex divide-x" style={{ borderColor: PEERSONA_DIVIDER }}>
            <button
              type="button"
              onClick={onLearnMore}
              className="font-open-sans flex flex-1 items-center justify-center gap-1 bg-white py-2.5 text-sm font-semibold transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-300"
              style={{ color: PEERSONA_TEXT_TERTIARY }}
            >
              <span>Learn more about Fi</span>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path
                  d="M9 6l6 6-6 6"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <button
              type="button"
              onClick={toggleResources}
              aria-expanded={resourcesOpen}
              className={cn(
                'font-open-sans flex flex-1 items-center justify-center gap-1 bg-white py-2.5 text-sm font-semibold transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-300',
                highlightResources && !resourcesOpen ? 'bg-orange-50/60' : '',
              )}
              style={{ color: PEERSONA_TEXT_TERTIARY }}
            >
              <span>View organizational resources</span>
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden
                className={resourcesOpen ? 'rotate-180' : ''}
              >
                <path
                  d="M6 9l6 6 6-6"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={toggleResources}
            aria-expanded={resourcesOpen}
            className="font-open-sans flex w-full items-center justify-center gap-1 bg-white py-2.5 text-sm font-semibold transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-300"
            style={{ color: PEERSONA_TEXT_TERTIARY }}
          >
            <span>View organizational resources</span>
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden
              className={resourcesOpen ? 'rotate-180' : ''}
            >
              <path
                d="M6 9l6 6 6-6"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}
        <PeersonaResourcesPanel
          open={resourcesOpen}
          resources={resources}
          isLoading={isLoading}
          isResorting={isResorting}
          onClose={closeResources}
        />
      </div>
    </>
  );
};
